'use client';

import * as React from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { cn } from '@/components/ui/utils';

export type StandMetricItem = {
  value: string;
  label: string;
  detail: string;
};

function MetricCard({ item, className }: { item: StandMetricItem; className?: string }) {
  return (
    <div
      className={cn(
        'flex h-full min-h-[210px] flex-col justify-between rounded-xl border border-white/[0.08] bg-[#1c1c1c] p-5 sm:min-h-[232px] sm:p-6 md:min-h-[248px]',
        className,
      )}
    >
      <p className="text-[1.8rem] tabular-nums leading-none tracking-tight text-white sm:text-[2rem] md:text-[2.125rem]">
        {item.value}
      </p>
      <div>
        <p className="mt-4 text-sm font-medium leading-snug text-white">{item.label}</p>
        <p className="mt-auto pt-3 text-xs leading-relaxed text-neutral-400 sm:pt-4">
          {item.detail}
        </p>
      </div>
    </div>
  );
}

export interface SustainabilityEnvironmentStandMetricsClientProps {
  title: string;
  subtitle?: string;
  metrics: StandMetricItem[];
  /** Environment page: 4 cards top row, remainder centered on second row (desktop). */
  desktopLayout?: 'single-row' | 'split-rows';
}

export default function SustainabilityEnvironmentStandMetricsClient({
  title,
  subtitle,
  metrics,
  desktopLayout = 'single-row',
}: SustainabilityEnvironmentStandMetricsClientProps) {
  const carouselLabelId = React.useId();
  const useSplitDesktop = desktopLayout === 'split-rows';
  const topRowMetrics = metrics.slice(0, 4);
  const bottomRowMetrics = metrics.slice(4);

  const getBottomColumnStart = (index: number, count: number) => {
    const cardSpan = 3;
    const totalSpan = count * cardSpan;
    const rowOffset = Math.floor((12 - totalSpan) / 2) + 1;
    return rowOffset + index * cardSpan;
  };

  return (
    <section className="relative bg-black text-white" aria-labelledby={carouselLabelId}>
      <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-10">
        <h2
          id={carouselLabelId}
          className="text-center text-[1.75rem] font-bold tracking-tight text-white sm:text-4xl md:text-[2.35rem]"
        >
          {title}
        </h2>
        {subtitle ? (
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm leading-relaxed text-neutral-300 sm:text-[0.9375rem]">
            {subtitle}
          </p>
        ) : null}

        {/* Carousel: &lt; lg */}
        <div className="relative mt-8 sm:mt-10 lg:hidden">
          <Carousel
            opts={{
              align: 'start',
              loop: false,
              dragFree: false,
              skipSnaps: false,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-3 sm:-ml-4">
              {metrics.map((item) => (
                <CarouselItem
                  key={item.label}
                  className="basis-[86%] pl-3 sm:basis-[48%] sm:pl-4 md:basis-[32%] lg:basis-[31%]"
                >
                  <MetricCard item={item} />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious
              variant="outline"
              className="left-1 top-1/2 z-[2] size-9 -translate-y-1/2 border-white/20 bg-black/75 text-white shadow-md backdrop-blur-[2px] hover:bg-black/90 hover:text-white disabled:opacity-25 sm:left-2"
            />
            <CarouselNext
              variant="outline"
              className="right-1 top-1/2 z-[2] size-9 -translate-y-1/2 border-white/20 bg-black/75 text-white shadow-md backdrop-blur-[2px] hover:bg-black/90 hover:text-white disabled:opacity-25 sm:right-2"
            />
          </Carousel>
        </div>

        {/* Desktop: single row, or split 4 + centered remainder */}
        {useSplitDesktop ? (
          <div className="mt-10 hidden space-y-4 lg:block">
            <ul className="grid grid-cols-12 gap-4">
              {topRowMetrics.map((item) => (
                <li key={item.label} className="col-span-3 min-w-0 lg:col-span-3">
                  <MetricCard item={item} />
                </li>
              ))}
            </ul>
            {bottomRowMetrics.length > 0 ? (
              <ul className="grid grid-cols-12 gap-4">
                {bottomRowMetrics.map((item, idx) => (
                  <li
                    key={item.label}
                    className="col-span-3 min-w-0"
                    style={{ gridColumnStart: getBottomColumnStart(idx, bottomRowMetrics.length) }}
                  >
                    <MetricCard item={item} />
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : (
          <ul
            className="mt-10 hidden gap-4 lg:grid"
            style={{ gridTemplateColumns: `repeat(${metrics.length}, minmax(0, 1fr))` }}
          >
            {metrics.map((item) => (
              <li key={item.label} className="min-w-0">
                <MetricCard item={item} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
